import { Injectable, Inject, PLATFORM_ID, TransferState, makeStateKey, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { isPlatformServer } from '@angular/common';
import { catchError, firstValueFrom, of, timeout } from 'rxjs';
import { TenantService } from '../tenant/tenant';
import { API_BASE_URL } from '../config/api-config';
import { environment } from '../../../environments/environment';
import { IExternalCountryResponse, IExternalCountryDetails, IService } from '../models/external-country.model';
import { IDocumentPricesResponse } from '../models/document-prices.model';
import { ISection, ISeoDocument } from '../models/documents.model';
import { SeoService } from './seo.service';

export interface IServicePriceData {
    totalPrice: string;
    totalDays: string;
    services?: IService[];
    documentPrices?: IDocumentPricesResponse | null;
}

@Injectable({
    providedIn: 'root',
})
export class GetCountryDocuments {
    private seoService = inject(SeoService);
    private servicePricesCache = new Map<string, Promise<IServicePriceData | null>>();
    readonly latestServicePrices = signal<IServicePriceData | null>(null);

    constructor(
        private http: HttpClient,
        private transferState: TransferState,
        @Inject(PLATFORM_ID) private platformId: Object,
        private tenantService: TenantService,
        @Inject(API_BASE_URL) private apiBaseUrl: string
    ) {}

    async getExternalCountry(externalCountry: number): Promise<IExternalCountryDetails | null> {
        let domain = this.tenantService.getDomain();

        if (!domain || domain === 'localhost' || domain === '127.0.0.1' || domain.includes('efrx')) {
            domain = environment.fallbackDomain;
        }

        const cacheKey = makeStateKey<IExternalCountryDetails>(`external-country-${externalCountry}-${domain}`);

        // 1. Browser check
        if (this.transferState.hasKey(cacheKey)) {
            const cachedData = this.transferState.get(cacheKey, null);
            this.transferState.remove(cacheKey);
            return cachedData;
        }

        // 2. Fetch via internal proxy
        try {
            const response = await firstValueFrom(
                this.http.get<IExternalCountryResponse>(
                    `${this.apiBaseUrl}/internal-api/${domain}/external-countries/${externalCountry}`
                ).pipe(
                    timeout(8000),
                    catchError((err) => {
                        console.error('[GetCountryDocuments] HTTP error fetching external country:', err);
                        return of(null);
                    })
                )
            );

            const countryDetails = response?.data || null;

            // 3. Save to TransferState on server
            if (countryDetails && isPlatformServer(this.platformId)) {
                this.transferState.set(cacheKey, countryDetails);
            }

            return countryDetails;
        } catch (error) {
            console.error(`Failed to load external country details for domain ${domain} and country ${externalCountry}`, error);
            return null;
        }
    }

    async getDocumentPrices(
        countryDocumentCaseId: number,
        dotNetCountryId: number,
        count: number = 1
    ): Promise<IDocumentPricesResponse | null> {
        let domain = this.tenantService.getDomain();

        if (!domain || domain === 'localhost' || domain === '127.0.0.1' || domain.includes('efrx')) {
            domain = environment.fallbackDomain;
        }

        const cacheKey = makeStateKey<IDocumentPricesResponse>(`document-prices-${countryDocumentCaseId}-${dotNetCountryId}-${count}-${domain}`);

        // 1. Browser check / TransferState retrieval
        if (this.transferState.hasKey(cacheKey)) {
            const cachedData = this.transferState.get(cacheKey, null);
            this.transferState.remove(cacheKey);
            return cachedData;
        }

        try {
            const response = await firstValueFrom(
                this.http.post<IDocumentPricesResponse>(
                    `${this.apiBaseUrl}/internal-api/${domain}/get-document-prices`,
                    {
                        countryDocumentCaseId,
                        dotNetCountryId,
                        count,
                    }
                ).pipe(
                    timeout(8000),
                    catchError((err) => {
                        console.error('[GetCountryDocuments] HTTP error fetching document prices:', err);
                        return of(null);
                    })
                )
            );

            // 2. Save to TransferState on server
            if (response && isPlatformServer(this.platformId)) {
                this.transferState.set(cacheKey, response);
            }

            return response || null;
        } catch (error) {
            console.error(`Failed to load document prices for countryDocumentCaseId ${countryDocumentCaseId}, ${domain}`, error);
            return null;
        }
    }

    /**
     * Scans content/sections or uses explicit IDs for #totalPrice / #totalDays placeholders.
     * Fetches document prices from the API using orderId and updates SEO data if provided.
     * Deduplicates API requests by countryDocumentCaseId & dotNetCountryId.
     */
    async fetchServicePricesIfNeeded(
        contentOrSectionsOrCaseId: string | ISection[] | ISection[][] | number,
        seoDataOrDotNetId?: ISeoDocument | ISeoDocument[] | number | null
    ): Promise<IServicePriceData | null> {
        let countryDocumentCaseId: number | null = null;
        let dotNetCountryId: number | null = null;
        let seoData: ISeoDocument | ISeoDocument[] | null = null;

        if (typeof contentOrSectionsOrCaseId === 'number' && typeof seoDataOrDotNetId === 'number') {
            countryDocumentCaseId = contentOrSectionsOrCaseId;
            dotNetCountryId = seoDataOrDotNetId;
        } else {
            seoData = seoDataOrDotNetId as (ISeoDocument | ISeoDocument[] | null);
            let allContent = '';
            if (typeof contentOrSectionsOrCaseId === 'string') {
                allContent = contentOrSectionsOrCaseId;
            } else if (Array.isArray(contentOrSectionsOrCaseId)) {
                if (contentOrSectionsOrCaseId.length > 0 && Array.isArray(contentOrSectionsOrCaseId[0])) {
                    allContent = (contentOrSectionsOrCaseId as ISection[][]).flat().map((s) => s.content || '').join('');
                } else {
                    allContent = (contentOrSectionsOrCaseId as ISection[]).map((s) => s.content || '').join('');
                }
            }

            // Parse orderId values from raw HTML content
            const orderIdTag = allContent.match(/<input[^>]*class="orderId"[^>]*>/i);
            if (!orderIdTag) return null;

            const idMatch = orderIdTag[0].match(/data-id="(\d+)"/);
            const dotNetMatch = orderIdTag[0].match(/data-dotnetcountryid="(\d+)"/);
            if (!idMatch || !dotNetMatch) return null;

            countryDocumentCaseId = Number(idMatch[1]);
            dotNetCountryId = Number(dotNetMatch[1]);
        }

        if (countryDocumentCaseId === null || dotNetCountryId === null) {
            return null;
        }

        const cacheKey = `${countryDocumentCaseId}-${dotNetCountryId}`;

        // Check if this countryDocumentCaseId & dotNetCountryId pair was already requested
        if (this.servicePricesCache.has(cacheKey)) {
            const cachedData = await this.servicePricesCache.get(cacheKey)!;
            if (cachedData) {
                this.latestServicePrices.set(cachedData);
                if (seoData) {
                    const currentSeo = Array.isArray(seoData) ? seoData[0] || null : seoData || null;
                    if (currentSeo) {
                        const seoDoc: ISeoDocument = {
                            ...currentSeo,
                            meta_title: (currentSeo.meta_title || '').replaceAll('#totalPrice', cachedData.totalPrice).replaceAll('#totalDays', cachedData.totalDays),
                            meta_description: (currentSeo.meta_description || '').replaceAll('#totalPrice', cachedData.totalPrice).replaceAll('#totalDays', cachedData.totalDays),
                            meta_keywords: (currentSeo.meta_keywords || '').replaceAll('#totalPrice', cachedData.totalPrice).replaceAll('#totalDays', cachedData.totalDays),
                        };
                        this.seoService.setPageSeo(seoDoc);
                    }
                }
            }
            return cachedData;
        }

        const fetchPromise = (async () => {
            try {
                const res = await this.getDocumentPrices(countryDocumentCaseId!, dotNetCountryId!, 1);
                if (res) {
                    let totalFee = 0;
                    let totalTime = 0;

                    res.services.forEach((service) => {
                        totalFee += service.ExpeditedOptions == null ? service.Fee : service.Fee + service.ExpeditedOptions.UsingExpeditedFees;
                        totalTime += service.ExpeditedOptions == null ? service.Time : service.ExpeditedOptions.ExpeditedWorkingDays;
                    });

                    const priceData: IServicePriceData = {
                        totalPrice: `${totalFee}`,
                        totalDays: totalTime + ' business days',
                        services: res.services,
                        documentPrices: res,
                    };

                    this.latestServicePrices.set(priceData);

                    const currentSeo = Array.isArray(seoData) ? seoData[0] || null : seoData || null;

                    if (currentSeo) {
                        const totalPrice = `${totalFee}`;
                        const totalDays = `${totalTime}`;

                        const seoDoc: ISeoDocument = {
                            ...currentSeo,
                            meta_title: (currentSeo.meta_title || '').replaceAll('#totalPrice', totalPrice).replaceAll('#totalDays', totalDays),
                            meta_description: (currentSeo.meta_description || '').replaceAll('#totalPrice', totalPrice).replaceAll('#totalDays', totalDays),
                            meta_keywords: (currentSeo.meta_keywords || '').replaceAll('#totalPrice', totalPrice).replaceAll('#totalDays', totalDays),
                        };

                        this.seoService.setPageSeo(seoDoc);
                    }

                    return priceData;
                }
            } catch (error) {
                console.error('[GetCountryDocuments] Error fetching service prices for placeholders:', error);
                this.servicePricesCache.delete(cacheKey);
            }
            return null;
        })();

        this.servicePricesCache.set(cacheKey, fetchPromise);
        return await fetchPromise;
    }
}

